import { useState, useRef } from "react";
import styles from "./generalInfoCard.module.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  IconBuildingCommunity,
  IconEdit,
  IconX,
  IconCircleCheck,
  IconCamera,
  IconAlertCircle,
  IconLoader2,
  IconFileUpload,
} from "@tabler/icons-react";
import { ChevronDown } from "../../../../constants/svg";
import {
  clientUploadLogo,
  companyInfo,
} from "../../../../services/clientCalls";
import { errorHandler } from "../../../../utils";
import { convertToBase64, isValidURL } from "../../../../utils/functions";
import CompanyAvatar from "../../../../assets/svgs/companyAvatar.svg";
import Button from "../../Button/button";
import { COUNTRIES } from "../../../Landing/components/Checkout/countries";

const toDateInputValue = (value) => {
  if (!value) return "";
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  let year = parsed.getUTCFullYear();
  if (year >= 0 && year < 100) year += 2000;
  const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const day = String(parsed.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isNigeriaLocation = (location) => {
  const key = String(location || "").trim().toLowerCase();
  if (!key) return false;
  return (
    key === "nigeria" ||
    key === "ng" ||
    key === "nga" ||
    key.includes("nigeria") ||
    /(^|[\s,])ng([\s,]|$)/.test(key)
  );
};

const GeneralInfoCard = ({ profileData, getCompanyProfile }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [load, setLoad] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [shakeUrl, setShakeUrl] = useState(false);
  const [incorpDoc, setIncorpDoc] = useState(null);
  const fileInputRef = useRef(null);

  const hasNonNgKyc = Boolean(
    (editing ? draft : profileData)?.companyRegisterationNumber ||
      (editing ? draft : profileData)?.taxOrVatNumber ||
      (editing ? draft : profileData)?.certificateOfIncorporation,
  );
  const activeLocation = editing ? draft?.location : profileData?.location;
  const isNigeria =
    isNigeriaLocation(activeLocation) ||
    (!String(activeLocation || "").trim() && !hasNonNgKyc);
  const showNigeriaFields = isNigeria;

  const handleChange = (key) => (e) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  const empty = (val) => !val;

  const handleEdit = () => {
    if (editing) {
      setEditing(false);
      setDraft({});
      setFile(null);
      setUrlError("");
    } else {
      setDraft({
        companyName: profileData?.companyName ?? "",
        companyWebsiteUrl: profileData?.companyWebsiteUrl ?? "",
        yearOfEstablishment: toDateInputValue(profileData?.yearOfEstablishment),
        companyAddress: profileData?.companyAddress ?? "",
        taxIdentificationNumber: profileData?.taxIdentificationNumber ?? "",
        cacNumber: profileData?.cacNumber ?? "",
        employeeIdentificationNumber:
          profileData?.employeeIdentificationNumber ?? "",
        position: profileData?.position ?? "",
        location: profileData?.location ?? "",
        companyRegisterationNumber:
          profileData?.companyRegisterationNumber ?? "",
        taxOrVatNumber: profileData?.taxOrVatNumber ?? "",
        certificateOfIncorporation:
          profileData?.certificateOfIncorporation ?? "",
      });
      setEditing(true);
      setUrlError("");
    }
  };

  const handleUpdate = async () => {
    if (draft.companyWebsiteUrl && !isValidURL(draft.companyWebsiteUrl)) {
      setUrlError(
        "Please enter a valid website address (e.g. www.example.com)",
      );
      setShakeUrl(true);
      setTimeout(() => setShakeUrl(false), 600);
      return;
    }
    setUrlError("");
    setLoad(true);

    try {
      if (file) {
        const base64Image = await convertToBase64(file);
        const response = await clientUploadLogo(
          { clientLogo: base64Image },
          setProgress,
        );
        if (response?.data?.success) {
          getCompanyProfile();
          window.dispatchEvent(new Event("client-profile-updated"));
        }
      }

      if (incorpDoc) {
        const base64Image = await convertToBase64(incorpDoc);
        draft.certificateOfIncorporation = base64Image;
      }

      const payload = {
        ...draft,
        yearOfEstablishment: toDateInputValue(draft.yearOfEstablishment),
      };

      const res = await companyInfo(payload);
      if (res.data) {
        getCompanyProfile();
        window.dispatchEvent(new Event("client-profile-updated"));
      }

      setEditing(false);
      setDraft({});
      setFile(null);
      setProgress(0);
    } catch (error) {
      error?.response?.data
        ? errorHandler(error.response.data.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong. Please check your internet connection.",
            "Error",
          );
    } finally {
      setLoad(false);
    }
  };

  const handleLogoClick = () => {
    if (editing) fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) setFile(e.target.files[0]);
    e.target.value = "";
  };

  const logoPreviewSrc =
    editing && file
      ? URL.createObjectURL(file)
      : profileData?.clientLogo || null;

  return (
    <div className={styles.card} id="genInfoCard">
      {/* ── Card header ── */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconBuildingCommunity size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>General Information</div>
            <div className={styles.cardSubtitle}>
              Core company details and registration info
            </div>
          </div>
        </div>

        {load ? (
          <div className={styles.loadingWrap}>
            <IconLoader2 size={20} className={styles.spinner} />
            {progress > 0 && progress < 100 && (
              <span className={styles.progressText}>{progress}%</span>
            )}
          </div>
        ) : (
          <div
            className={`${styles.btnGroup} ${editing ? styles.btnGroupEditing : ""}`}
          >
            {editing && (
              <Button
                variant="secondary"
                leftIcon={<IconCircleCheck size={14} />}
                size="sm"
                onClick={handleUpdate}
                disabled={load}
                loading={load}
              >
                Update
              </Button>
            )}
            <Button
              leftIcon={editing ? <IconX size={14} /> : <IconEdit size={14} />}
              size="sm"
              onClick={handleEdit}
            >
              {editing ? "Cancel" : "Edit"}
            </Button>
          </div>
        )}
      </div>

      {/* ── Card body ── */}
      <div className={styles.cardBody}>
        {/* Logo */}
        <div className={styles.logoSection}>
          <div
            className={`${styles.logoPreview} ${editing ? styles.logoPreviewEditing : ""}`}
            id="cardLogoContainer"
            onClick={handleLogoClick}
            title={editing ? "Click to change logo" : ""}
          >
            {logoPreviewSrc ? (
              <img src={logoPreviewSrc} alt="Company logo" />
            ) : (
              <img src={CompanyAvatar} alt="Company logo" />
            )}
            <div className={styles.logoOverlay}>
              <IconCamera size={20} />
              <span>Change</span>
            </div>
            <span className={styles.logoEditBadge} aria-hidden="true">
              <IconCamera size={11} />
            </span>
          </div>
          <div className={styles.logoInfo}>
            <div className={styles.logoInfoTitle}>Company Logo</div>
            <p>PNG or JPG, min 200x200px recommended</p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {/* ── Info grid ── */}
        <div className={styles.infoGrid}>
          {/* Company Name */}
          <div className={styles.infoField}>
            <span className={styles.infoLabel}>Company Name</span>
            {!editing && (
              <span
                className={
                  empty(profileData?.companyName)
                    ? styles.infoValueEmpty
                    : styles.infoValue
                }
              >
                {profileData?.companyName || "Not provided"}
              </span>
            )}
            {editing && (
              <input
                className={styles.editInput}
                type="text"
                value={draft.companyName}
                onChange={handleChange("companyName")}
              />
            )}
          </div>

          {/* Company Website */}
          <div className={styles.infoField}>
            <span className={styles.infoLabel}>Company Website</span>
            {!editing &&
              (empty(profileData?.companyWebsiteUrl) ? (
                <span className={styles.infoValueEmpty}>Not provided</span>
              ) : (
                <span className={styles.infoValue}>
                  <a
                    href={
                      profileData.companyWebsiteUrl.startsWith("http")
                        ? profileData.companyWebsiteUrl
                        : `https://${profileData.companyWebsiteUrl}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {profileData.companyWebsiteUrl}
                  </a>
                </span>
              ))}
            {editing && (
              <>
                <input
                  className={[
                    styles.editInput,
                    urlError ? styles.editInputError : "",
                    shakeUrl ? styles.shake : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  type="text"
                  value={draft.companyWebsiteUrl}
                  placeholder="e.g. www.yourcompany.com"
                  onChange={(e) => {
                    setDraft((d) => ({
                      ...d,
                      companyWebsiteUrl: e.target.value,
                    }));
                    setUrlError("");
                  }}
                />
                {urlError && (
                  <div className={styles.fieldError}>
                    <IconAlertCircle size={13} /> {urlError}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Date Founded */}
          <div className={styles.infoField}>
            <span className={styles.infoLabel}>Date Founded</span>
            {!editing && (
              <span
                className={
                  empty(profileData?.yearOfEstablishment)
                    ? styles.infoValueEmpty
                    : styles.infoValue
                }
              >
                {profileData?.yearOfEstablishment
                  ? moment(toDateInputValue(profileData.yearOfEstablishment)).format(
                      "Do MMM YYYY",
                    )
                  : "Not provided"}
              </span>
            )}
            {editing && (
              <input
                className={styles.editInput}
                type="date"
                value={draft.yearOfEstablishment}
                onChange={handleChange("yearOfEstablishment")}
              />
            )}
          </div>

          {/* Company Address */}
          <div className={styles.infoField}>
            <span className={styles.infoLabel}>Company Address</span>
            {!editing && (
              <span
                className={
                  empty(profileData?.companyAddress)
                    ? styles.infoValueEmpty
                    : styles.infoValue
                }
              >
                {profileData?.companyAddress || "Not provided"}
              </span>
            )}
            {editing && (
              <input
                className={styles.editInput}
                type="text"
                value={draft.companyAddress}
                onChange={handleChange("companyAddress")}
              />
            )}
          </div>

          {/* Position */}
          <div className={styles.infoField}>
            <span className={styles.infoLabel}>Position</span>
            {!editing && (
              <span
                className={
                  empty(profileData?.position)
                    ? styles.infoValueEmpty
                    : styles.infoValue
                }
              >
                {profileData?.position || "Not provided"}
              </span>
            )}
            {editing && (
              <div className={styles.selectWrap}>
                <select
                  className={styles.editInput}
                  value={draft.position}
                  onChange={handleChange("position")}
                >
                  <option value="" disabled>
                    Select Position
                  </option>
                  <option value="CTO">CTO</option>
                  <option value="Manager">Manager</option>
                  <option value="Director">Director</option>
                  <option value="Recruiter">Recruiter</option>
                  <option value="Team Lead">Team Lead</option>
                  <option value="Senior Executive">Senior Executive</option>
                  <option value="CFO">CFO</option>
                  <option value="Business Owner">Business Owner</option>
                  <option value="Entrepreneur">Entrepreneur</option>
                  <option value="Managing Director">Managing Director</option>
                  <option value="Project Manager">Project Manager</option>
                  <option value="Human Resources Manager">
                    Human Resources Manager
                  </option>
                  <option value="Technical Officer">Technical Officer</option>
                </select>
                <ChevronDown />
              </div>
            )}
          </div>

          {/* Location */}
          <div className={styles.infoField}>
            <span className={styles.infoLabel}>Location</span>
            {!editing && (
              <span
                className={
                  empty(profileData?.location)
                    ? styles.infoValueEmpty
                    : styles.infoValue
                }
              >
                {profileData?.location || "Not provided"}
              </span>
            )}
            {editing && (
              <select
                className={styles.editInput}
                value={draft.location}
                onChange={handleChange("location")}
              >
                <option value="" disabled>
                  Select Location
                </option>
                {COUNTRIES?.map((p) => (
                  <option key={p.iso} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* location-dependent fields  */}
          {showNigeriaFields ? (
            <>
              {/* TIN Number */}
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>TIN Number</span>
                {!editing && (
                  <span
                    className={
                      empty(profileData?.taxIdentificationNumber)
                        ? styles.infoValueEmpty
                        : styles.infoValue
                    }
                  >
                    {profileData?.taxIdentificationNumber || "Not provided"}
                  </span>
                )}
                {editing && (
                  <input
                    className={styles.editInput}
                    type="text"
                    inputMode="numeric"
                    value={draft.taxIdentificationNumber}
                    onChange={handleChange("taxIdentificationNumber")}
                  />
                )}
              </div>

              {/* CAC Number */}
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>CAC Number</span>
                {!editing && (
                  <span
                    className={
                      empty(profileData?.cacNumber)
                        ? styles.infoValueEmpty
                        : styles.infoValue
                    }
                  >
                    {profileData?.cacNumber || "Not provided"}
                  </span>
                )}
                {editing && (
                  <input
                    className={styles.editInput}
                    type="text"
                    inputMode="numeric"
                    value={draft.cacNumber}
                    onChange={handleChange("cacNumber")}
                  />
                )}
              </div>

              {/* EI Number */}
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>EI Number</span>
                {!editing && (
                  <span
                    className={
                      empty(profileData?.employeeIdentificationNumber)
                        ? styles.infoValueEmpty
                        : styles.infoValue
                    }
                  >
                    {profileData?.employeeIdentificationNumber ||
                      "Not provided"}
                  </span>
                )}
                {editing && (
                  <input
                    className={styles.editInput}
                    type="text"
                    inputMode="numeric"
                    value={draft.employeeIdentificationNumber}
                    onChange={handleChange("employeeIdentificationNumber")}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              {/* Company Registration Number */}
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>
                  Company Registration Number
                </span>
                {!editing && (
                  <span
                    className={
                      empty(profileData?.companyRegisterationNumber)
                        ? styles.infoValueEmpty
                        : styles.infoValue
                    }
                  >
                    {profileData?.companyRegisterationNumber ||
                      "Not provided"}
                  </span>
                )}
                {editing && (
                  <input
                    className={styles.editInput}
                    type="text"
                    value={draft.companyRegisterationNumber}
                    onChange={handleChange("companyRegisterationNumber")}
                  />
                )}
              </div>

              {/* Tax ID / VAT Number */}
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>Tax ID / VAT Number</span>
                {!editing && (
                  <span
                    className={
                      empty(profileData?.taxIdentificationNumber)
                        ? styles.infoValueEmpty
                        : styles.infoValue
                    }
                  >
                    {profileData?.taxIdentificationNumber || "Not provided"}
                  </span>
                )}
                {editing && (
                  <input
                    className={styles.editInput}
                    type="text"
                    value={draft.taxIdentificationNumber}
                    onChange={handleChange("taxIdentificationNumber")}
                  />
                )}
              </div>
            </>
          )}

          {/* Certificate of Incorporation */}
          {editing && !isNigeria && (
            <div className="flex flex-col gap-1.5">
              <label className={styles.infoLabel} htmlFor="incorpDoc">
                <span>
                  Certificate of Incorporation{" "}
                  <span className="text-[8px]">(optional)</span>
                </span>
              </label>
              <label
                htmlFor="incorpDoc"
                className={`${styles.docUpload} ${
                  incorpDoc ||
                  draft?.certificateOfIncorporation ||
                  profileData?.certificateOfIncorporation
                    ? styles.hasFile
                    : ""
                }`}
              >
                <IconFileUpload size={17} />
                <span>
                  {incorpDoc
                    ? incorpDoc.name
                    : draft?.certificateOfIncorporation ||
                        profileData?.certificateOfIncorporation
                      ? "File already uploaded"
                      : "Upload PDF"}
                </span>
              </label>
              <input
                id="incorpDoc"
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    setIncorpDoc(e.target.files[0]);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralInfoCard;
